/**
 * Transform CollectionController response to component-compatible format.
 * Accepts both:
 *   - new: { data: { items: [{ id, data: {...} }], pagination: {...} } }
 *   - legacy: { docs: [...] }
 */
export const transformApiData = (apiResponse: any) => {
  // New CollectionController shape: { success, data: { items, pagination } }
  const envelope = apiResponse?.data ?? apiResponse;
  const rawItems: any[] = envelope?.items ?? apiResponse?.docs ?? [];

  return rawItems.map((item: any) => {
    // CollectionController wraps fields inside item.data
    const d = item.data ?? item;
    const id = item.id ?? item._id;
    return {
      id,
      _id: id,
      name: d.name,
      description: d.description,
      courseImage: d.courseImage,
      // levelId is a lookup — joined docs come back as item.__lookup_levelId
      level: item.__lookup_levelId?.[0]?.data ?? d.level ?? null,
      format: d.format || 'online',
      price: d.price || 0,
      maxStudents: d.maxStudents,
      startDate: formatDate(d.startDate),
      classDays: d.classDays || [],
      timeSlots: d.timeSlots || [],
      enrolledCount: d.enrolledCount || 0,
      status: d.status || 'open',
      category: d.category,
    };
  });
};

/**
 * Extract pagination from CollectionController response.
 */
export const extractPagination = (apiResponse: any) => {
  const p = apiResponse?.data?.pagination ?? {};
  return {
    totalDocs: p.total ?? 0,
    limit:     p.limit ?? 10,
    totalPages: p.pages ?? 0,
    page:      p.page ?? 1,
    hasPrevPage: (p.page ?? 1) > 1,
    hasNextPage: (p.page ?? 1) < (p.pages ?? 0),
  };
};

/**
 * Format date from ISO string to Vietnamese format
 */
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return isoDate;
  }
};

/**
 * Format time from ISO string to HH:MM
 */
export const formatTime = (isoTime: string): string => {
  if (!isoTime) return '';
  try {
    const date = new Date(isoTime);
    return date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return isoTime;
  }
};

/**
 * Get status badge color
 */
export const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    open: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    full: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    closed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return colors[status] || colors.open;
};

/**
 * Get status label
 */
export const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    open: 'Đang mở đăng ký',
    full: 'Đã đầy',
    closed: 'Đã kết thúc',
  };
  return labels[status] || status;
};
